import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Mail, Trash2, LayoutGrid, List, Search, ShieldCheck, Shield } from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import ConfirmationModal from './ConfirmationModal';
import CandidateVerificationModal from './CandidateVerificationModal';
import VerificationDetailsModal from './VerificationDetailsModal';

import { API_BASE } from '../config';

// const API_BASE = "http://127.0.0.1:8000";

export default function TalentVault({ onPreview, searchTerm, setSearchTerm }) {
    const [resumes, setResumes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('talentVaultView') || 'list');

    // Verification Modal State
    const [verifyModalOpen, setVerifyModalOpen] = useState(false);
    const [verifyCandidate, setVerifyCandidate] = useState(null);
    const [verifyPrefill, setVerifyPrefill] = useState(null); // { type: 'reference'|'identity', refUrl, docUrl }

    // View Proof Modal State
    const [viewProofOpen, setViewProofOpen] = useState(false);
    const [viewProofCandidate, setViewProofCandidate] = useState(null);

    useEffect(() => {
        localStorage.setItem('talentVaultView', viewMode);
    }, [viewMode]);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const openVerifyModal = (e, candidate, prefill = null) => {
        e.stopPropagation();
        setVerifyCandidate(candidate);
        setVerifyPrefill(prefill);
        setVerifyModalOpen(true);
    };

    const openViewProofModal = (e, candidate) => {
        e.stopPropagation();
        setViewProofCandidate(candidate);
        setViewProofOpen(true);
    }

    const handleVerifyComplete = () => {
        setVerifyModalOpen(false);
        fetchResumes(); // Refresh list to show verified status
    };

    const fetchResumes = async () => {
        try {
            let url = `${API_BASE}/resumes?limit=100`;
            if (startDate) url += `&start_date=${startDate}`;
            if (endDate) url += `&end_date=${endDate}`;

            const res = await axios.get(url);
            setResumes(res.data);
        } catch (err) {
            console.error(err);
            toast.error("Failed to load resumes.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchResumes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [deleteId, setDeleteId] = useState(null);

    const handleDeleteClick = (id) => {
        setDeleteId(id);
    };

    const confirmDelete = async () => {
        if (!deleteId) return;
        try {
            await axios.delete(`${API_BASE}/resume/${deleteId}`);
            setResumes(prev => prev.filter(r => r._id !== deleteId));
            toast.success("Deleted successfully");
        } catch (e) {
            toast.error("Delete failed");
        }
        setDeleteId(null);
    };

    const filteredResumes = resumes.filter(r => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
            (r.name && r.name.toLowerCase().includes(term)) ||
            (r.email && r.email.toLowerCase().includes(term)) ||
            (r.skills && r.skills.some(s => s.toLowerCase().includes(term)))
        );
    });

    if (loading) return <div className="flex justify-center p-10"><Loader2 className="w-8 h-8 animate-spin text-brand-500" /></div>;

    return (
        <div className="space-y-6">
            <div className="text-center py-8">
                <h1 className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400 mb-4 tracking-tight">
                    Talent Vault
                </h1>
                <p className="text-slate-400 text-lg">Manage your entire candidate pool ({resumes.length})</p>
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-6">

                <div className="flex items-center gap-3 w-full sm:w-auto">
                    <div className="relative group flex-1 sm:flex-none">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-brand-500 transition-colors" />
                        <input
                            type="text"
                            placeholder="Search candidates..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full sm:w-64 pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200"
                        />
                    </div>

                    <div className="flex items-center bg-white border border-slate-200 rounded-lg p-1 dark:bg-slate-800 dark:border-slate-700">
                        <button
                            onClick={() => setViewMode('list')}
                            className={clsx(
                                "p-1.5 rounded-md transition-all",
                                viewMode === 'list' ? "bg-slate-100 text-slate-900 shadow-sm dark:bg-slate-700 dark:text-slate-100" : "text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                            )}
                            title="List View"
                        >
                            <List className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => setViewMode('grid')}
                            className={clsx(
                                "p-1.5 rounded-md transition-all",
                                viewMode === 'grid' ? "bg-slate-100 text-slate-900 shadow-sm dark:bg-slate-700 dark:text-slate-100" : "text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                            )}
                            title="Grid View"
                        >
                            <LayoutGrid className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="flex items-center gap-2">
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="text-xs border border-slate-200 rounded px-2 py-1.5 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300"
                        />
                        <span className="text-slate-300">-</span>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="text-xs border border-slate-200 rounded px-2 py-1.5 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300"
                        />
                    </div>

                    <button onClick={fetchResumes} className="px-3 py-2 text-sm font-medium text-brand-600 bg-brand-50 rounded-lg hover:bg-brand-100 transition-colors dark:bg-brand-900/20 dark:text-brand-300 dark:hover:bg-brand-900/30 whitespace-nowrap">
                        Refresh / Filter
                    </button>
                </div>
            </div>

            {viewMode === 'list' ? (
                <div className="glass-card overflow-hidden dark:bg-slate-800 dark:border-slate-700">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm text-slate-600 dark:text-slate-300">
                            <thead className="bg-slate-50 border-b border-slate-200 dark:bg-slate-700/50 dark:border-slate-700">
                                <tr>
                                    <th className="px-6 py-4 font-semibold">Candidate</th>
                                    <th className="px-6 py-4 font-semibold">Experience</th>
                                    <th className="px-6 py-4 font-semibold">Uploaded</th>
                                    <th className="px-6 py-4 font-semibold">Skills</th>
                                    <th className="px-6 py-4 font-semibold">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                {filteredResumes.map((r, i) => (
                                    <motion.tr
                                        key={r._id}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: i * 0.05 }}
                                        onClick={() => onPreview(r._id, r.name, r)}
                                        className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors group cursor-pointer"
                                    >
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-brand-100 text-brand-600 flex items-center justify-center font-bold text-lg dark:bg-brand-900/30 dark:text-brand-300">
                                                    {r.name ? r.name.charAt(0).toUpperCase() : '?'}
                                                </div>
                                                <div>
                                                    <div className="font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                                                        {r.name || "Unknown"}
                                                        {r.is_verified && (
                                                            <button
                                                                onClick={(e) => openViewProofModal(e, r)}
                                                                className="hover:scale-110 transition-transform cursor-pointer focus:outline-none"
                                                                title="Verified - Click to View Proof"
                                                            >
                                                                <ShieldCheck className="w-4 h-4 text-green-500 fill-current" />
                                                            </button>
                                                        )}
                                                    </div>
                                                    <div className="text-xs text-slate-400 flex items-center gap-1">
                                                        <Mail className="w-3 h-3" /> {r.email || "No email"}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300">
                                                {r.years_experience} Years
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-xs text-slate-500 dark:text-slate-400">
                                            {r.uploaded_at ? new Date(r.uploaded_at).toLocaleDateString() : '-'}
                                        </td>
                                        <td className="px-6 py-4 max-w-xs">
                                            <div className="flex flex-wrap gap-1 mt-1">
                                                {(() => {
                                                    const skills = r.skills || [];
                                                    const sortedSkills = [...skills].sort((a, b) => {
                                                        const aMatch = searchTerm && a.toLowerCase().includes(searchTerm.toLowerCase());
                                                        const bMatch = searchTerm && b.toLowerCase().includes(searchTerm.toLowerCase());
                                                        if (aMatch && !bMatch) return -1;
                                                        if (!aMatch && bMatch) return 1;
                                                        return 0;
                                                    });
                                                    return sortedSkills.slice(0, 3).map((s, idx) => {
                                                        const isHighlighted = searchTerm && s.toLowerCase().includes(searchTerm.toLowerCase());
                                                        return (
                                                            <span
                                                                key={idx}
                                                                className={clsx(
                                                                    "text-[10px] px-2 py-0.5 border rounded-full transition-all",
                                                                    isHighlighted
                                                                        ? "bg-brand-600 text-white border-brand-600 shadow-sm font-medium scale-105"
                                                                        : "dark:border-slate-600"
                                                                )}
                                                            >
                                                                {s}
                                                            </span>
                                                        );
                                                    });
                                                })()}
                                                {(r.skills || []).length > 3 && (
                                                    <span className="text-[10px] text-slate-400">+{r.skills.length - 3}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2 opacity-60 group-hover:opacity-100 transition-opacity">
                                                {!r.is_verified && (
                                                    <button
                                                        onClick={(e) => openVerifyModal(e, r)}
                                                        className="p-1.5 text-slate-500 hover:text-brand-600 hover:bg-brand-50 rounded-md transition-colors dark:hover:bg-brand-900/30 dark:hover:text-brand-400"
                                                        title="Verify Identity"
                                                    >
                                                        <Shield className="w-4 h-4" />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleDeleteClick(r._id); }}
                                                    className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors dark:hover:bg-red-900/30 dark:hover:text-red-400"
                                                    title="Delete"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </motion.tr>
                                ))}
                                {filteredResumes.length === 0 && (
                                    <tr>
                                        <td colSpan="4" className="text-center py-10 text-slate-400">
                                            {searchTerm ? "No matches found." : "No resumes found. Import some first!"}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredResumes.map((r, i) => (
                        <motion.div
                            key={r._id}
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: i * 0.05 }}
                            onClick={() => onPreview(r._id, r.name, r)}
                            className="glass-card p-5 hover:shadow-lg transition-shadow dark:bg-slate-800 dark:border-slate-700 flex flex-col justify-between cursor-pointer"
                        >
                            <div>
                                <div className="flex justify-between items-start mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 rounded-full bg-brand-100 text-brand-600 flex items-center justify-center font-bold text-xl dark:bg-brand-900/30 dark:text-brand-300">
                                            {r.name ? r.name.charAt(0).toUpperCase() : '?'}
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-slate-900 dark:text-slate-100 flex items-center gap-1">
                                                <span className="truncate max-w-[140px]" title={r.name}>
                                                    {r.name || "Unknown"}
                                                </span>
                                                {r.is_verified && (
                                                    <button
                                                        onClick={(e) => openViewProofModal(e, r)}
                                                        className="hover:scale-110 transition-transform cursor-pointer focus:outline-none shrink-0"
                                                        title="Verified - Click to View Proof"
                                                    >
                                                        <ShieldCheck className="w-4 h-4 text-green-500 fill-current" />
                                                    </button>
                                                )}
                                            </h3>
                                            <div className="flex flex-col">
                                                <span className="text-xs text-slate-500 dark:text-slate-400">{r.years_experience} Years Exp.</span>
                                                {r.uploaded_at && <span className="text-[10px] text-slate-400">{new Date(r.uploaded_at).toLocaleDateString()}</span>}
                                            </div>
                                        </div>
                                    </div>
                                    {/* Actions in Grid */}
                                    <div className="flex gap-1">
                                        {!r.is_verified && (
                                            <button
                                                onClick={(e) => openVerifyModal(e, r)}
                                                className="p-1.5 text-slate-400 hover:text-brand-600 hover:bg-brand-50 rounded-md transition-colors dark:hover:bg-brand-900/30"
                                                title="Verify Identity"
                                            >
                                                <Shield className="w-4 h-4" />
                                            </button>
                                        )}
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleDeleteClick(r._id); }}
                                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors dark:hover:bg-red-900/30"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 mb-2 truncate">
                                        <Mail className="w-3 h-3 flex-shrink-0" />
                                        <span className="truncate">{r.email || "No email"}</span>
                                    </div>
                                </div>

                                <div className="flex flex-wrap gap-1.5 mt-auto">
                                    {(() => {
                                        const skills = r.skills || [];
                                        const sortedSkills = [...skills].sort((a, b) => {
                                            const aMatch = searchTerm && a.toLowerCase().includes(searchTerm.toLowerCase());
                                            const bMatch = searchTerm && b.toLowerCase().includes(searchTerm.toLowerCase());
                                            if (aMatch && !bMatch) return -1;
                                            if (!aMatch && bMatch) return 1;
                                            return 0;
                                        });
                                        return sortedSkills.slice(0, 5).map((s, idx) => {
                                            const isHighlighted = searchTerm && s.toLowerCase().includes(searchTerm.toLowerCase());
                                            return (
                                                <span
                                                    key={idx}
                                                    className={clsx(
                                                        "text-[10px] px-2 py-1 border rounded-md transition-all",
                                                        isHighlighted
                                                            ? "bg-brand-600 text-white border-brand-600 shadow-sm font-medium scale-105"
                                                            : "bg-slate-50 border-slate-100 text-slate-600 dark:bg-slate-700/50 dark:border-slate-600 dark:text-slate-300"
                                                    )}
                                                >
                                                    {s}
                                                </span>
                                            );
                                        });
                                    })()}
                                    {(r.skills || []).length > 5 && (
                                        <span className="text-[10px] text-slate-400 self-center">+{r.skills.length - 5}</span>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    ))}
                    {filteredResumes.length === 0 && (
                        <div className="col-span-full text-center py-20 text-slate-400">
                            {searchTerm ? "No matches found." : "No resumes found. Import some first!"}
                        </div>
                    )}
                </div>
            )}

            <ConfirmationModal
                isOpen={!!deleteId}
                onClose={() => setDeleteId(null)}
                onConfirm={confirmDelete}
                title="Delete Candidate?"
                message="Are you sure you want to delete this candidate from the vault? This action cannot be undone."
                confirmText="Delete Candidate"
            />

            <CandidateVerificationModal
                isOpen={verifyModalOpen}
                onClose={() => setVerifyModalOpen(false)}
                resumeId={verifyCandidate?._id}
                candidateName={verifyCandidate?.name}
                onVerifyComplete={handleVerifyComplete}
                prefillData={verifyPrefill}
            />

            <VerificationDetailsModal
                isOpen={viewProofOpen}
                onClose={() => setViewProofOpen(false)}
                candidate={viewProofCandidate}
                onReverify={(type, refUrl, docUrl) => {
                    setViewProofOpen(false);
                    openVerifyModal({ stopPropagation: () => { } }, viewProofCandidate, { type, refUrl, docUrl });
                }}
            />
        </div>
    );
}
